/**
 * RotationBlockedRow — one element the rotation did NOT move, and why.
 * For Media Devoted | Created By Ciro Autiero
 *
 * A no-stats skip is NOT a problem — it's an element we deliberately left alone
 * for lack of data: neutral (muted). A pair-hold ("swap waiting for a queued
 * replacement") is calm/informational: blue. A genuine pool-floor block is
 * amber. An UNMANAGED block is red and gets the CloudOff icon of the
 * "not connected to the bank" banner, because it is the same story: the
 * capacity ranking put this element below the cut, but it lives on the tracker
 * with no bank row, so the optimizer cannot move it — and the fix is the Sync
 * button on that banner.
 */

import { FC } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  CircleSlash,
  CloudOff,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import type { RotationElementDiff } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { IdChip } from '../RotationSwapRow';

const BLOCKED_STYLE = {
  unmanaged: {
    Icon: CloudOff,
    box: 'border-destructive/40 bg-destructive/10',
    icon: 'text-destructive',
    label: 'Not linked to the bank',
  },
  noStats: {
    Icon: CircleSlash,
    box: 'border-border/60 bg-muted/40',
    icon: 'text-muted-foreground',
    label: 'Ignored — no stats',
  },
  pairHold: {
    Icon: ArrowLeftRight,
    box: 'border-primary/30 bg-primary/5',
    icon: 'text-primary',
    label: 'Waiting for a replacement',
  },
  held: {
    Icon: AlertTriangle,
    box: 'border-warning/40 bg-warning/10',
    icon: 'text-warning',
    label: 'Blocked',
  },
  // MD-1147: an offer leaving / rejoining its path for its partner cap.
  capPause: {
    Icon: PauseCircle,
    box: 'border-warning/40 bg-warning/10',
    icon: 'text-warning',
    label: 'Paused for offer cap',
  },
  capReactivate: {
    Icon: PlayCircle,
    box: 'border-success/40 bg-success/10',
    icon: 'text-success',
    label: 'Reactivated after offer cap',
  },
} as const;

function styleFor(diff: RotationElementDiff) {
  if (diff.action === 'cap-pause') return BLOCKED_STYLE.capPause;
  if (diff.action === 'cap-reactivate') return BLOCKED_STYLE.capReactivate;
  if (diff.unmanaged) return BLOCKED_STYLE.unmanaged;
  if (diff.noStats) return BLOCKED_STYLE.noStats;
  if (diff.pairHold) return BLOCKED_STYLE.pairHold;
  return BLOCKED_STYLE.held;
}

export const RotationBlockedRow: FC<{ diff: RotationElementDiff }> = ({ diff }) => {
  const style = styleFor(diff);
  return (
    <div className={cn('rounded-md border px-2.5 py-1.5', style.box)}>
      <p className="flex items-center gap-1.5 text-xs font-semibold">
        <style.Icon
          className={cn('h-3.5 w-3.5 shrink-0', style.icon)}
          aria-label={style.label}
        />
        <span className="truncate" title={diff.itemName}>
          {diff.itemName}
        </span>
        {diff.offerName ? (
          <span className="truncate text-[11px] font-normal text-muted-foreground" title={diff.offerName}>
            · {diff.offerName}
          </span>
        ) : null}
        {diff.unmanaged ? <IdChip itemId={diff.itemId} /> : null}
      </p>
      {diff.reason ? (
        <span className="text-[10px] text-muted-foreground">{diff.reason}</span>
      ) : null}
    </div>
  );
};
